import { useEffect, useRef, useState } from "react";
import { createChart, CandlestickSeries, HistogramSeries, LineSeries, LineStyle } from "lightweight-charts";
import api from "@/lib/api";
import { X, Plus, Check, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AnalysisModal from "@/components/AnalysisModal";

const RANGES = [
  { id: "1d", label: "1H" },
  { id: "5d", label: "5H" },
  { id: "1mo", label: "1B" },
  { id: "3mo", label: "3B" },
  { id: "6mo", label: "6B" },
];

const Toggle = ({ on, onClick, children, color, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${on ? "border-transparent text-[#07090E]" : "bg-[var(--surface-base)] text-[var(--text-secondary)] border-[var(--border)]"}`} style={on ? { backgroundColor: color } : {}}>
    {children}
  </button>
);

export default function StockChartModal({ ticker, name, onClose }) {
  const { user } = useAuth();
  const priceRef = useRef(null);
  const rsiRef = useRef(null);
  const macdRef = useRef(null);
  const [range, setRange] = useState("1mo");
  const [loading, setLoading] = useState(true);
  const [inWatch, setInWatch] = useState(false);
  const [meta, setMeta] = useState({ last: null, change_pct: null });
  const [showMA, setShowMA] = useState(true);
  const [showMACD, setShowMACD] = useState(true);
  const [analyze, setAnalyze] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/watchlist").then((r) => setInWatch((r.data.symbols || []).includes(ticker))).catch(() => {});
  }, [ticker, user]);

  useEffect(() => {
    let priceChart, rsiChart, macdChart, ro1, ro2, ro3;
    let cancelled = false;
    const draw = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/candles/${ticker}?range=${range}`);
        const candles = res.data.candles || [];
        if (cancelled || !priceRef.current) return;
        priceRef.current.innerHTML = "";
        rsiRef.current.innerHTML = "";
        if (macdRef.current) macdRef.current.innerHTML = "";

        const common = {
          layout: { background: { color: "transparent" }, textColor: "#94A3B8", fontFamily: "JetBrains Mono, monospace" },
          localization: { locale: "en-US" },
          grid: { vertLines: { color: "rgba(33,42,58,0.4)" }, horzLines: { color: "rgba(33,42,58,0.4)" } },
          timeScale: { borderColor: "#212A3A", timeVisible: range === "1d" || range === "5d" },
          rightPriceScale: { borderColor: "#212A3A" },
          crosshair: { mode: 1 },
        };

        priceChart = createChart(priceRef.current, { ...common, height: 320, width: priceRef.current.clientWidth });
        const candleSeries = priceChart.addSeries(CandlestickSeries, {
          upColor: "#10B981", downColor: "#F43F5E", borderUpColor: "#10B981", borderDownColor: "#F43F5E", wickUpColor: "#10B981", wickDownColor: "#F43F5E",
        });
        candleSeries.setData(candles.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close })));

        if (showMA) {
          const ma20 = priceChart.addSeries(LineSeries, { color: "#F59E0B", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
          ma20.setData(candles.filter((c) => c.ma20 != null).map((c) => ({ time: c.time, value: c.ma20 })));
          const ma50 = priceChart.addSeries(LineSeries, { color: "#60A5FA", lineWidth: 1.5, priceLineVisible: false, lastValueVisible: false });
          ma50.setData(candles.filter((c) => c.ma50 != null).map((c) => ({ time: c.time, value: c.ma50 })));
        }

        const volSeries = priceChart.addSeries(HistogramSeries, { priceFormat: { type: "volume" }, priceScaleId: "vol" });
        priceChart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        volSeries.setData(candles.map((c) => ({ time: c.time, value: c.volume, color: c.close >= c.open ? "rgba(16,185,129,0.4)" : "rgba(244,63,94,0.4)" })));
        priceChart.timeScale().fitContent();

        rsiChart = createChart(rsiRef.current, { ...common, height: 110, width: rsiRef.current.clientWidth });
        const rsiSeries = rsiChart.addSeries(LineSeries, { color: "#00E599", lineWidth: 2, lastValueVisible: false });
        rsiSeries.setData(candles.filter((c) => c.rsi != null).map((c) => ({ time: c.time, value: c.rsi })));
        rsiSeries.createPriceLine({ price: 70, color: "#F43F5E", lineWidth: 1, lineStyle: LineStyle.Dashed, title: "70" });
        rsiSeries.createPriceLine({ price: 30, color: "#10B981", lineWidth: 1, lineStyle: LineStyle.Dashed, title: "30" });
        rsiChart.timeScale().fitContent();

        const charts = [priceChart, rsiChart];
        if (showMACD && macdRef.current) {
          macdChart = createChart(macdRef.current, { ...common, height: 110, width: macdRef.current.clientWidth });
          const histSeries = macdChart.addSeries(HistogramSeries, {});
          histSeries.setData(candles.filter((c) => c.hist != null).map((c) => ({ time: c.time, value: c.hist, color: c.hist >= 0 ? "rgba(16,185,129,0.6)" : "rgba(244,63,94,0.6)" })));
          const macdLine = macdChart.addSeries(LineSeries, { color: "#00E599", lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false });
          macdLine.setData(candles.filter((c) => c.macd != null).map((c) => ({ time: c.time, value: c.macd })));
          const sigLine = macdChart.addSeries(LineSeries, { color: "#F59E0B", lineWidth: 1.5, lastValueVisible: false, priceLineVisible: false });
          sigLine.setData(candles.filter((c) => c.signal != null).map((c) => ({ time: c.time, value: c.signal })));
          macdChart.timeScale().fitContent();
          charts.push(macdChart);
        }

        // sync time scales across panes
        charts.forEach((src) => {
          src.timeScale().subscribeVisibleLogicalRangeChange((lr) => {
            if (!lr) return;
            charts.forEach((tgt) => { if (tgt !== src) tgt.timeScale().setVisibleLogicalRange(lr); });
          });
        });

        if (candles.length) {
          const last = candles[candles.length - 1];
          const first = candles[0];
          setMeta({ last: last.close, change_pct: first.close ? (((last.close - first.close) / first.close) * 100).toFixed(2) : null });
        }

        const attach = (chart, el) => { const ro = new ResizeObserver(() => chart.applyOptions({ width: el.clientWidth })); ro.observe(el); return ro; };
        ro1 = attach(priceChart, priceRef.current);
        ro2 = attach(rsiChart, rsiRef.current);
        if (macdChart) ro3 = attach(macdChart, macdRef.current);
      } catch (e) {
        toast.error("Gagal memuat grafik");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    draw();
    return () => {
      cancelled = true;
      try { ro1?.disconnect(); ro2?.disconnect(); ro3?.disconnect(); } catch (e) {}
      try { priceChart?.remove(); rsiChart?.remove(); macdChart?.remove(); } catch (e) {}
    };
  }, [ticker, range, showMA, showMACD]);

  const toggleWatch = async () => {
    if (!user) { toast.error("Masuk dulu untuk menyimpan ke watchlist"); return; }
    try {
      if (inWatch) { await api.delete(`/watchlist/${ticker}`); setInWatch(false); toast.success(`${ticker} dihapus dari watchlist`); }
      else { await api.post("/watchlist", { symbol: ticker }); setInWatch(true); toast.success(`${ticker} ditambahkan ke watchlist`); }
      window.dispatchEvent(new Event("watchlist-updated"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memperbarui watchlist"); }
  };

  const up = meta.change_pct != null && parseFloat(meta.change_pct) >= 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="chart-modal">
      <div className="card w-full max-w-4xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="font-heading text-2xl font-extrabold">{ticker}</h3>
              {meta.last != null && <span className="font-mono-data text-lg">{meta.last.toLocaleString("id-ID")}</span>}
              {meta.change_pct != null && <span className={`font-mono-data text-sm ${up ? "text-bull" : "text-bear"}`}>{up ? "+" : ""}{meta.change_pct}%</span>}
            </div>
            <p className="text-xs text-[var(--text-secondary)]">{name || "Bursa Efek Indonesia"} · candle · MA20/50 · volume · RSI(14) · MACD</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAnalyze(true)} data-testid="chart-analyze-btn" className="h-9 px-3 rounded-lg text-sm font-semibold flex items-center gap-1.5 bg-[var(--accent)]/15 text-accent border border-[var(--accent)]/30 hover:bg-[var(--accent)]/25 transition-colors">
              <Sparkles size={15} /> Analisis AI
            </button>
            <button onClick={toggleWatch} data-testid="chart-watch-toggle" className={`h-9 px-3 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-colors ${inWatch ? "bg-[var(--accent)]/15 text-accent border border-[var(--accent)]/30" : "bg-[var(--surface-hover)] border border-[var(--border)]"}`}>
              {inWatch ? <Check size={15} /> : <Plus size={15} />} {inWatch ? "Tersimpan" : "Watchlist"}
            </button>
            <button onClick={onClose} data-testid="chart-close" className="p-2 rounded-md hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"><X size={18} /></button>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex gap-1.5">
            {RANGES.map((r) => (
              <button key={r.id} onClick={() => setRange(r.id)} data-testid={`range-${r.id}`} className={`px-3 py-1 rounded-md text-xs font-semibold border transition-colors ${range === r.id ? "bg-[var(--accent)] text-[#07090E] border-[var(--accent)]" : "bg-[var(--surface-base)] text-[var(--text-secondary)] border-[var(--border)] hover:bg-[var(--surface-hover)]"}`}>{r.label}</button>
            ))}
          </div>
          <div className="flex gap-1.5">
            <Toggle on={showMA} onClick={() => setShowMA((v) => !v)} color="#F59E0B" testid="toggle-ma">MA 20/50</Toggle>
            <Toggle on={showMACD} onClick={() => setShowMACD((v) => !v)} color="#00E599" testid="toggle-macd">MACD</Toggle>
          </div>
        </div>

        <div className="relative">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--surface-card)]/60">
              <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <div ref={priceRef} className="w-full" data-testid="candle-chart" />
          <div className="flex items-center gap-3 text-[10px] font-mono-data text-[var(--text-muted)] mt-1">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#F59E0B] inline-block" /> MA20</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#60A5FA] inline-block" /> MA50</span>
          </div>
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-2 mb-1">RSI (14)</p>
          <div ref={rsiRef} className="w-full" data-testid="rsi-chart" />
          {showMACD && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-2 mb-1">MACD (12, 26, 9)</p>
              <div ref={macdRef} className="w-full" data-testid="macd-chart" />
            </>
          )}
        </div>
      </div>
      {analyze && <AnalysisModal ticker={ticker} onClose={() => setAnalyze(false)} />}
    </div>
  );
}
